import { ShieldAlert } from 'lucide-react';
import { Outlet } from 'react-router';
import { Card } from '#client/components/ui/Card';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Page } from '#client/components/ui/Page';
import { useCan } from '#client/hooks/useCan';
import type { Permission } from '#shared/types';

/**
 * A pathless route wrapper that gates a group of routes on one permission, or
 * on any one of a list.
 *
 * This is presentation, not authorisation — every route it guards is also
 * gated server-side by a `@Requires(...)` decorator. Its job is to give someone
 * who deep-links or bookmarks a screen they cannot use a clear explanation
 * instead of a wall of 403 toasts from a page that half-renders.
 *
 * It deliberately renders an explanation rather than redirecting: silently
 * bouncing someone to another screen reads as a broken link.
 */
export function RequireCapability({
  capability,
}: {
  capability: Permission | readonly Permission[];
}) {
  const can = useCan();
  const allowed =
    typeof capability === 'string'
      ? can(capability)
      : capability.some((permission) => can(permission));

  if (allowed) return <Outlet />;

  return (
    <Page title="No access">
      <Card>
        <EmptyState
          icon={<ShieldAlert />}
          title="You do not have access to this screen"
          body="Your role does not include this area. If you need it, ask an administrator to grant the permission."
        />
      </Card>
    </Page>
  );
}
